import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, Link2, KeyRound, Send, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mobile-money operators supported by Rafiki (matches backend models/rafiki.py).
const RAFIKI_OPERATORS = [
  'SAFARICOM', 'AT', 'AIRTEL', 'VODACOM', 'TIGO', 'HALOTEL', 'TTCL', 'MTN',
  'VODAFONE', 'MOOV', 'ORANGE', 'FREE', 'EXPRESSO', 'WAVE', 'BKASH', 'NAGAD',
  'GCASH', 'MAYA', 'COINS', 'GRABPAY', 'EASYPAISA', 'JAZZCASH', 'UPAISA',
  'SADAPAY', 'NAYAPAY', 'FINJA', 'PAYMAX',
];

const PAYOUT_STATUS = {
  completed: { text: 'Completed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  accepted: { text: 'Accepted', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  pending: { text: 'Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  submitted: { text: 'Submitted', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  failed: { text: 'Failed', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  rejected: { text: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const STATUS_LABEL = {
  not_configured: { text: 'Not Configured', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  token_pending: { text: 'Token Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  ready: { text: 'Ready', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
};

const CONN_STATUS = {
  connected: { text: 'Connected', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  invalid_token: { text: 'Invalid Token', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  forbidden: { text: 'Forbidden (scope)', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  not_configured: { text: 'Not Configured', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  api_error: { text: 'API Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  network_error: { text: 'Network Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  timeout: { text: 'Timeout', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const RafikiSettingsCard = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [token, setToken] = useState('');
  const [connResult, setConnResult] = useState(null);

  // Send Test Payout (sandbox-only) form state
  const [payout, setPayout] = useState({
    environment: 'sandbox',
    type: 'MOBILE_MONEY',
    amount: '100', currency: 'KES', country: 'KE',
    holder_name: 'John Doe', holder_type: 'INDIVIDUAL',
    phone_number: '+254700000000', operator: 'SAFARICOM',
    bank_id: '', account_number: '',
    purpose: 'REMITTANCE',
  });
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [payoutError, setPayoutError] = useState('');
  const [payoutResult, setPayoutResult] = useState(null);

  const setPayoutField = (k, v) => setPayout((p) => ({ ...p, [k]: v }));

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/config`);
      setConfig(res.data);
    } catch (e) {
      console.error('Error fetching Rafiki config:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      const payload = { enabled: true };
      if (token.trim()) payload.api_token = token.trim();
      const res = await axios.put(`${API_URL}/api/admin/payments/rafiki/config`, payload);
      setConfig(res.data);
      setToken('');
      setSuccess('Rafiki Sandbox configuration saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setConnResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/connection`);
      setConnResult(res.data);
    } catch (e) {
      setConnResult({ status: 'api_error', message: e.response?.data?.detail || 'Request failed' });
    } finally {
      setTesting(false);
    }
  };

  const status = STATUS_LABEL[config?.status] || STATUS_LABEL.not_configured;

  const loadBanks = useCallback(async () => {
    if (banks.length || banksLoading) return;
    setBanksLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/banks`);
      setBanks(res.data?.banks || []);
    } catch (e) {
      setBanks([]);
    } finally {
      setBanksLoading(false);
    }
  }, [banks.length, banksLoading]);

  const onPayoutTypeChange = (t) => {
    setPayoutField('type', t);
    setPayoutError(''); setPayoutResult(null);
    if (t === 'BANK_ACCOUNT') loadBanks();
  };

  const validatePayout = () => {
    const amt = parseFloat(payout.amount);
    if (!payout.amount || isNaN(amt) || amt <= 0) return 'Amount must be a positive number.';
    if (!/^[A-Za-z]{3}$/.test(payout.currency.trim())) return 'Currency must be a 3-letter code (e.g. KES).';
    if (!/^[A-Za-z]{2}$/.test(payout.country.trim())) return 'Country must be a 2-letter code (e.g. KE).';
    if (!payout.holder_name.trim()) return 'Recipient name is required.';
    if (payout.type === 'BANK_ACCOUNT') {
      if (!payout.bank_id) return 'Select a bank.';
      if (!payout.account_number.trim()) return 'Account number is required.';
    } else {
      if (!/^\+\d{8,15}$/.test(payout.phone_number.trim())) return 'Phone number must be E.164 (e.g. +254700000000).';
      if (!RAFIKI_OPERATORS.includes(payout.operator)) return 'Select a valid mobile-money operator.';
    }
    return '';
  };

  const sendPayout = async (e) => {
    e.preventDefault();
    setPayoutError(''); setPayoutResult(null);
    const v = validatePayout();
    if (v) { setPayoutError(v); return; }
    setSending(true);
    try {
      const body = {
        environment: payout.environment,
        type: payout.type,
        amount: payout.amount.trim(),
        currency: payout.currency.trim().toUpperCase(),
        country: payout.country.trim().toUpperCase(),
        holder_name: payout.holder_name.trim(),
      };
      if (payout.type === 'BANK_ACCOUNT') {
        body.holder_type = payout.holder_type;
        body.bank_id = payout.bank_id;
        body.account_number = payout.account_number.trim();
      } else {
        body.phone_number = payout.phone_number.trim();
        body.operator = payout.operator;
      }
      if (payout.purpose) body.purpose = payout.purpose;
      const res = await axios.post(`${API_URL}/api/admin/payments/rafiki/payouts`, body);
      setPayoutResult(res.data);
    } catch (e) {
      // No transaction result shown on request failure -> never a false success.
      setPayoutError(e.response?.data?.detail
        ? (typeof e.response.data.detail === 'string' ? e.response.data.detail : 'Validation failed. Check the fields.')
        : 'Failed to send test payout.');
    } finally {
      setSending(false);
    }
  };

  const payoutStatus = payoutResult
    ? (PAYOUT_STATUS[payoutResult.status] || { text: payoutResult.status, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' })
    : null;
  const payoutDetail = (() => {
    const d = payoutResult?.failure_reason?.detail;
    if (!d) return '';
    if (typeof d === 'string') return d;
    const parts = [];
    if (d.message) parts.push(d.message);
    const fields = d.errors?.fields;
    if (fields && typeof fields === 'object') {
      Object.entries(fields).forEach(([k, v]) => parts.push(`${k}: ${Array.isArray(v) ? v.join(', ') : v}`));
    }
    return parts.join(' — ') || d.code || '';
  })();
  const inputCls = 'w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500';

  return (
    <div data-testid="rafiki-settings-card" className="space-y-6">
      {error && (
        <div data-testid="rafiki-settings-error" className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5" /><span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div data-testid="rafiki-settings-success" className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
          <CheckCircle2 className="w-5 h-5" /><span className="text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Rafiki Sandbox Configuration</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs font-medium rounded border bg-slate-500/20 text-slate-400 border-slate-500/30">Sandbox</span>
            <span data-testid="rafiki-status-badge" className={`px-3 py-1 text-xs font-medium rounded border ${status.color}`}>
              {status.text}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Rafiki Sandbox API Key
              <span
                data-testid="rafiki-token-status-badge"
                className={`ml-2 px-2 py-0.5 text-xs font-medium rounded border ${
                  config?.token_set
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                    : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                }`}
              >
                {config?.token_set ? 'Key configured' : 'Not configured'}
              </span>
            </label>
            <input
              type="password"
              data-testid="rafiki-api-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              placeholder={config?.token_set ? '•••••••• saved — paste a new key to replace' : 'Paste your Rafiki Sandbox API key (xxx.xxx.xxx)'}
              className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-amber-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Sandbox only. The key is encrypted at rest and never displayed again.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || loading}
          data-testid="rafiki-save-config-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Save Configuration
        </button>
      </form>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Rafiki Connection Test</h2>
          {connResult && (
            <span
              data-testid="rafiki-conn-status-badge"
              className={`px-3 py-1 text-xs font-medium rounded border ${(CONN_STATUS[connResult.status] || CONN_STATUS.api_error).color}`}
            >
              {(CONN_STATUS[connResult.status] || CONN_STATUS.api_error).text}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-400 mb-3">
          Verifies the stored key against the Rafiki Sandbox <code className="text-amber-300">/v1/payouts</code> endpoint.
        </p>
        {connResult && (
          <p className="text-sm text-slate-300 mb-3" data-testid="rafiki-conn-message">{connResult.message}</p>
        )}
        <button
          onClick={testConnection}
          disabled={testing}
          data-testid="rafiki-test-connection-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Test Connection
        </button>
      </div>

      {/* Send Test Payout — SANDBOX ONLY, reuses POST /rafiki/payouts + ledger */}
      <form onSubmit={sendPayout} data-testid="rafiki-payout-panel" className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Send className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">Send Test Payout</h2>
          <span data-testid="rafiki-payout-env-badge" className={`px-2 py-0.5 text-xs font-medium rounded border ${payout.environment === 'production' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
            {payout.environment === 'production' ? 'Production' : 'Sandbox'}
          </span>
        </div>
        <p className="text-sm text-slate-400 -mt-2">
          {payout.environment === 'production'
            ? <>Sends a real {payout.type} payout via Rafiki <span className="text-red-400 font-medium">Production</span>. This moves real money.</>
            : (payout.type === 'BANK_ACCOUNT'
                ? <>Sends a BANK_ACCOUNT payout to the Rafiki Sandbox. Tip: account number <code className="text-amber-300">0390195016650</code> simulates a FAILED payout.</>
                : <>Sends a MOBILE_MONEY payout to the Rafiki Sandbox. Tip: a phone number ending in <code className="text-amber-300">000100</code> simulates a FAILED payout.</>)}
        </p>

        {payoutError && (
          <div data-testid="rafiki-payout-error" className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            <AlertCircle className="w-5 h-5" /><span className="text-sm">{payoutError}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Environment</label>
            <select data-testid="rafiki-payout-environment" value={payout.environment}
              onChange={(e) => { setPayoutField('environment', e.target.value); setPayoutError(''); setPayoutResult(null); }} className={inputCls}>
              <option value="sandbox">Sandbox</option>
              <option value="production">Production</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Payout Type</label>
            <select data-testid="rafiki-payout-type" value={payout.type}
              onChange={(e) => onPayoutTypeChange(e.target.value)} className={inputCls}>
              <option value="MOBILE_MONEY">Mobile Money</option>
              <option value="BANK_ACCOUNT">Bank Account</option>
            </select>
          </div>
        </div>
        {payout.environment === 'production' && (
          <div data-testid="rafiki-payout-prod-warning" className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs">
            <ShieldAlert className="w-4 h-4" /> Production sends real money. Requires Owner to enable Production payouts.
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
            <input data-testid="rafiki-payout-amount" type="text" value={payout.amount}
              onChange={(e) => setPayoutField('amount', e.target.value)} placeholder="100" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Currency</label>
            <input data-testid="rafiki-payout-currency" type="text" value={payout.currency}
              onChange={(e) => setPayoutField('currency', e.target.value)} placeholder="KES" className={`${inputCls} uppercase`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
            <input data-testid="rafiki-payout-country" type="text" value={payout.country}
              onChange={(e) => setPayoutField('country', e.target.value)} placeholder="KE" className={`${inputCls} uppercase`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Recipient Name</label>
            <input data-testid="rafiki-payout-holder" type="text" value={payout.holder_name}
              onChange={(e) => setPayoutField('holder_name', e.target.value)} placeholder="John Doe" className={inputCls} />
          </div>

          {payout.type === 'MOBILE_MONEY' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Operator</label>
                <select data-testid="rafiki-payout-operator" value={payout.operator}
                  onChange={(e) => setPayoutField('operator', e.target.value)} className={inputCls}>
                  {RAFIKI_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone (E.164)</label>
                <input data-testid="rafiki-payout-phone" type="text" value={payout.phone_number}
                  onChange={(e) => setPayoutField('phone_number', e.target.value)} placeholder="+254700000000" className={`${inputCls} font-mono`} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Holder Type</label>
                <select data-testid="rafiki-payout-holder-type" value={payout.holder_type}
                  onChange={(e) => setPayoutField('holder_type', e.target.value)} className={inputCls}>
                  <option value="INDIVIDUAL">Individual</option>
                  <option value="BUSINESS">Business</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Bank</label>
                <select data-testid="rafiki-payout-bank" value={payout.bank_id}
                  onChange={(e) => setPayoutField('bank_id', e.target.value)} className={inputCls} disabled={banksLoading}>
                  <option value="">{banksLoading ? 'Loading banks…' : 'Select a bank'}</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.name}{b.country ? ` (${b.country})` : ''}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Account Number</label>
                <input data-testid="rafiki-payout-account-number" type="text" value={payout.account_number}
                  onChange={(e) => setPayoutField('account_number', e.target.value)} placeholder="12345678" className={`${inputCls} font-mono`} />
              </div>
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Purpose <span className="text-slate-500 font-normal">(optional)</span></label>
          <select data-testid="rafiki-payout-purpose" value={payout.purpose}
            onChange={(e) => setPayoutField('purpose', e.target.value)} className={inputCls}>
            <option value="">—</option>
            {['REMITTANCE','P2P_TRANSFER','SALARY_AND_WAGES','SERVICES_PAYMENT','GOODS_PURCHASE','BILLS_PAYMENT','INVOICE_PAYMENT','DONATION'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <button type="submit" disabled={sending} data-testid="rafiki-send-payout-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send Test Payout
        </button>

        {payoutResult && (
          <div data-testid="rafiki-payout-result" className="mt-2 p-4 bg-slate-900/50 border border-slate-700 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-300">Result</span>
              <span data-testid="rafiki-payout-status-badge" className={`px-3 py-1 text-xs font-medium rounded border ${payoutStatus.color}`}>
                {payoutStatus.text}
              </span>
            </div>
            <div className="text-xs text-slate-400 font-mono break-all space-y-1">
              <div>Transaction: {payoutResult.id}</div>
              {payoutResult.provider_response?.payout_id && <div>Rafiki payout: {payoutResult.provider_response.payout_id}</div>}
              {payoutResult.provider_response?.state && <div>State: {payoutResult.provider_response.state}</div>}
              {payoutResult.provider_response?.context && <div>Context: {payoutResult.provider_response.context}</div>}
              {payoutResult.provider_response?.error && (
                <div className="text-red-400">
                  Error: {payoutResult.provider_response.error}
                  {payoutResult.provider_response.http ? ` (HTTP ${payoutResult.provider_response.http})` : ''}
                </div>
              )}
              {payoutDetail && <div className="text-red-400">Detail: {payoutDetail}</div>}
              {payoutResult.failure_reason?.reason && !payoutResult.provider_response?.error && (
                <div className="text-red-400">Reason: {String(payoutResult.failure_reason.reason)}</div>
              )}
            </div>
          </div>
        )}
      </form>

      <RafikiProductionSection />
    </div>
  );
};


const RafikiProductionSection = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [conn, setConn] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const fetchCfg = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/production/config`);
      setCfg(res.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const saveKey = async (e) => {
    e.preventDefault(); setErr(''); setMsg(''); setSaving(true);
    try {
      const payload = {};
      if (token.trim()) payload.api_token = token.trim();
      const res = await axios.put(`${API_URL}/api/admin/payments/rafiki/production/config`, payload);
      setCfg(res.data); setToken(''); setMsg('Production configuration saved.');
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to save production configuration'); }
    finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true); setConn(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/rafiki/production/connection`);
      setConn(res.data);
    } catch (e) { setConn({ status: 'api_error', message: e.response?.data?.detail || 'Request failed' }); }
    finally { setTesting(false); }
  };

  const toggle = async (enabled) => {
    setErr(''); setMsg(''); setToggling(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/payments/rafiki/production/payouts-enabled`, { enabled });
      setCfg(res.data);
      setMsg(enabled ? 'Production payouts ENABLED.' : 'Production payouts disabled.');
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to change production payouts setting'); }
    finally { setToggling(false); }
  };

  const status = STATUS_LABEL[cfg?.status] || STATUS_LABEL.not_configured;
  const inputCls = 'w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-red-500';

  return (
    <div data-testid="rafiki-production-section" className="space-y-6 border-t border-slate-700/50 pt-6 mt-2">
      {err && <div data-testid="rafiki-prod-error" className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"><AlertCircle className="w-5 h-5" />{err}</div>}
      {msg && <div data-testid="rafiki-prod-success" className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm"><CheckCircle2 className="w-5 h-5" />{msg}</div>}

      <form onSubmit={saveKey} className="bg-slate-800/50 border border-red-700/30 rounded-xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-semibold text-white">Rafiki Production Configuration</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs font-medium rounded border bg-red-500/20 text-red-400 border-red-500/30">Production</span>
            <span data-testid="rafiki-prod-status-badge" className={`px-3 py-1 text-xs font-medium rounded border ${status.color}`}>{status.text}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Production API Key
              <span data-testid="rafiki-prod-token-status" className={`ml-2 px-2 py-0.5 text-xs font-medium rounded border ${cfg?.token_set ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                {cfg?.token_set ? 'Key configured' : 'Not configured'}
              </span>
            </label>
            <input type="password" data-testid="rafiki-prod-api-token-input" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off"
              placeholder={cfg?.token_set ? '•••••••• saved — paste a new key to replace' : 'Paste your Rafiki PRODUCTION API key'}
              className={inputCls} />
            <p className="text-xs text-slate-500 mt-1">Live environment. Encrypted at rest and never displayed again.</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button type="submit" disabled={saving || loading} data-testid="rafiki-prod-save-config-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Save Production Key
          </button>
          <button type="button" onClick={testConn} disabled={testing} data-testid="rafiki-prod-test-connection-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Test Connection
          </button>
          {conn && <span data-testid="rafiki-prod-conn-status" className={`px-3 py-1 text-xs font-medium rounded border ${(CONN_STATUS[conn.status] || CONN_STATUS.api_error).color}`}>{(CONN_STATUS[conn.status] || CONN_STATUS.api_error).text}</span>}
        </div>
        {conn && <p className="text-xs text-slate-400" data-testid="rafiki-prod-conn-message">{conn.message}</p>}

        <div className="flex items-center justify-between p-3 bg-slate-900/40 rounded-lg border border-slate-700/50">
          <div>
            <p className="text-sm font-medium text-white">Enable Production Payouts</p>
            <p className="text-xs text-slate-500">Owner-only. When ON, Production-environment payouts send real money.</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="rafiki-prod-payouts-state" className={`px-3 py-1 text-xs font-medium rounded border ${cfg?.payouts_enabled ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
              {cfg?.payouts_enabled ? 'ENABLED' : 'Disabled'}
            </span>
            <button type="button" onClick={() => toggle(!cfg?.payouts_enabled)} disabled={!isOwner || toggling}
              title={isOwner ? '' : 'Owner only'} data-testid="rafiki-prod-payouts-toggle-btn"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 ${cfg?.payouts_enabled ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-red-600 text-white hover:bg-red-500'}`}>
              {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {cfg?.payouts_enabled ? 'Disable' : 'Enable'}
            </button>
          </div>
        </div>
        {!isOwner && <p className="text-xs text-slate-500">Only an Owner can enable or disable Production payouts.</p>}
      </form>
    </div>
  );
};

export default RafikiSettingsCard;
